import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { formService } from '../services/formService';

/**
 * Hook that polls /api/maintenance/status every 30 seconds.
 * 
 * Phases:
 *   idle     → no maintenance scheduled or far away
 *   warning  → T-30 minutes: show warning popup
 *   saving   → T-5 minutes: force save draft + redirect
 *   redirected → already redirected to /maintenance
 * 
 * @param {Object} options
 * @param {boolean} options.enabled - Whether to enable polling (e.g., after form loads)
 * @param {boolean} options.hasToken - Whether user has a token for saving draft
 * @param {Function} options.onForceSave - Callback to trigger save draft
 * @returns {{ maintenanceSchedule, showWarning, warningMinutes, dismissWarning }}
 */
export function useMaintenanceCheck({ enabled = false, hasToken = false, onForceSave } = {}) {
  const navigate = useNavigate();
  const [maintenanceSchedule, setMaintenanceSchedule] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const [warningMinutes, setWarningMinutes] = useState(0);
  const phaseRef = useRef('idle'); // idle | warning | saving | redirected
  const intervalRef = useRef(null);

  const dismissWarning = useCallback(() => {
    setShowWarning(false);
  }, []);

  const checkMaintenance = useCallback(async () => {
    if (phaseRef.current === 'redirected') return;

    try {
      const status = await formService.getMaintenanceStatus();

      // Admin users bypass maintenance mode (server checks JWT cookie)
      if (status.isAdmin) {
        setMaintenanceSchedule(null);
        setShowWarning(false);
        phaseRef.current = 'idle';
        return;
      }

      if (!status.scheduled) {
        setMaintenanceSchedule(null);
        setShowWarning(false);
        phaseRef.current = 'idle';
        return;
      }

      setMaintenanceSchedule(status);

      // If maintenance is already active, redirect immediately
      if (status.isActive) {
        phaseRef.current = 'redirected';
        navigate('/maintenance', { replace: true });
        return;
      }

      const minutesLeft = status.minutesUntilStart;
      setWarningMinutes(minutesLeft);

      // T-5: Force save + redirect (5 minutes before maintenance)
      if (minutesLeft <= 5 && phaseRef.current !== 'saving') {
        phaseRef.current = 'saving';
        setShowWarning(false);

        if (hasToken && onForceSave) {
          try {
            await onForceSave();
          } catch (err) {
            console.error('[Maintenance] Force save failed:', err);
          }
        }

        phaseRef.current = 'redirected';
        navigate('/maintenance', { replace: true });
        return;
      }

      // T-30: Show warning (30 minutes before maintenance)
      if (minutesLeft <= 30 && phaseRef.current === 'idle') {
        phaseRef.current = 'warning';
        setShowWarning(true);
      }
    } catch (err) {
      // API unreachable — backend might be down already
      // Don't redirect here; let Nginx handle it with static maintenance.html
      console.warn('[Maintenance] Status check failed:', err.message);
    }
  }, [hasToken, onForceSave, navigate]);

  useEffect(() => {
    if (!enabled) return;

    // Initial check
    checkMaintenance();

    // Poll every 30 seconds
    intervalRef.current = setInterval(checkMaintenance, 30000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled, checkMaintenance]);

  return {
    maintenanceSchedule,
    showWarning,
    warningMinutes,
    dismissWarning,
  };
}
